import { useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import { CATEGORY_NAMES } from "constants/dispensary";
import MenuContext from "context/MenuContext";
import { MedicalIdCondition } from "custom-types/MedicalIdCondition";
import { getCurrentDispensary } from "redux/selectors/dispensary";

import ActiveDealsCarousel from "components/Dispensary/ActiveDealsCarousel";
import CategoryCarousel from "components/Dispensary/CategoryCarousel";
import LastUpdatedTime from "components/Dispensary/LastUpdatedTime";
import MedicalInfo from "components/Dispensary/MedicalInfo";
import PhenotypeCarousel from "components/Dispensary/PhenotypeCarousel";
import ProductRecTrays from "components/Dispensary/ProductRecTrays";
import PromotedBrandCarousel from "components/Dispensary/PromotedBrandCarousel";
import SearchBar from "components/Dispensary/SearchBar";
import ShopAllButton from "components/Dispensary/ShopAllButton";
import StaffPicksCarousel from "components/Dispensary/StaffPicksCarousel";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";

const PaidTierMenu: React.FC = () => {
  const {
    selectors: { menuFilters },
  } = useContext(MenuContext);

  const currentDispensary = useSelector(getCurrentDispensary);
  const isMedIdRequired =
    currentDispensary.orderMedIdCondition === MedicalIdCondition.Required;

  // Creates a sorted list of most popular categories followed by all other available categories
  // Used in the categories carousel
  const sortedCategories = useMemo(() => {
    const availableCategoryFilters = menuFilters?.find(
      (filter) => filter.name === "product_category",
    );

    if (!availableCategoryFilters) {
      return [];
    }

    const availableCategories = availableCategoryFilters.values?.map(
      (category) => category.value,
    );

    const popularCategories = Object.keys(CATEGORY_NAMES).reduce(
      (list: string[], categoryName) => {
        const matchingCategory = availableCategories?.find(
          (name) => categoryName === name,
        );
        return matchingCategory ? [...list, matchingCategory] : list;
      },
      [],
    );

    const otherCategories =
      availableCategories?.filter(
        (name) => !Object.keys(CATEGORY_NAMES).includes(name),
      ) || [];

    return [...popularCategories, ...otherCategories];
  }, [menuFilters]);

  return (
    <div className="bg-white pt-xl">
      <div className="container">
        {isMedIdRequired && <MedicalInfo />}

        <SearchBar className="mb-sm mt-md" routeToMenu />

        <LastUpdatedTime className="mb-xl" />

        <div className="flex flex-col gap-xl">
          <CategoryCarousel />

          <PromotedBrandCarousel />

          <RecentlyViewedMenuItems
            attributes={{ dispensarySlug: currentDispensary.slug }}
          />

          <ActiveDealsCarousel dealCardLinks />

          <StaffPicksCarousel />

          <ProductRecTrays categories={sortedCategories.slice(0, 1)} />

          <PhenotypeCarousel />

          <ProductRecTrays categories={sortedCategories.slice(1)} />

          <ShopAllButton />
        </div>
      </div>
    </div>
  );
};
export default PaidTierMenu;
